import {Row, Col} from "react-bootstrap"
import React, {Component} from "react"
import {isString} from "lodash"
import PropTypes from "prop-types"

import Form from "@irati/ui/src/Form"
import Select from "@irati/ui/src/FormItem/Select"
import {Firstname, Lastname, Email, Locale, ExternalID, Teams} from "@irati-web/fields/user"
import ValidationButton from "@irati/ui/src/FormItem/ValidationButton"
import Loading from "@irati/ui/src/Loading"
import {H2, H4} from "@irati/ui/src/Typography"
import {IconButton} from "@irati/ui/src/Button"
import mfaService from "@irati/auth/services/mfa"
import YouNeedToMfa from "@irati-web/mfa/components/Error/YouNeedToMfa"
import Hr from "@irati/ui/src/Layout/Hr"
import GenericModal from "@irati/ui/src/GenericModal"
import successImage from "@irati/ui/images/success.svg"
import {Para} from "@irati/ui/src/Labels"
import History from "@irati/utils/history"
import BURegion from "@irati-web/fields/team/BURegion"
import buService from "@irati/billing/services/bu"

import adminTracking from "@irati-tracking/admin"
import authentication from "@irati/auth"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import usersService from "@irati/identity/services/users"
import {hangPromise} from "@irati/utils/promise"
import teamService from "@irati/identity/services/teams"
import {NewUserInput} from "@irati/types/ops"
import {enumToArray} from "@irati/utils/enumToArray"

import BackLink from "../../../components/Users/BackLink"
import {VIEW_PEOPLE} from "../../../routes/routeConfig"

enum UserRole {
  user = "admin.containers.addOnePerson.fields.role.label.user",
  admin = "admin.containers.addOnePerson.fields.role.label.admin"
}

const RoleOptions = (formatMessage: InjectIntl["formatMessage"]) =>
  enumToArray(UserRole).map((role) => {
    return {label: formatMessage({id: role}), value: role}
  })

const MANUAL_ADD_SOURCE = "People - Manual add"

type Team = {
  value: string,
  label: string
}
interface Props extends InjectIntl {
  teams: Team[],
  goBack: () => void,
  gotoSuccess: () => void
}

interface State {
  firstName: string | null,
  lastName: string | null,
  email: string | null,
  userGroupId: string | null,
  language: string | null,
  access: string,
  externalUserId: string,
  form: {isLoading: boolean},
  teams: Team[],
  submit: Promise<any> | null,
  load: Promise<any>,
  allTeams: Team[] | [],
  saved: boolean,
  mfaLoad: Promise<any>,
  primaryTeamId: string | null,
  isCountryNew: boolean
}

class AddOnePerson extends Component<Props, State> {
  static contextTypes = {
    apps: PropTypes.object.isRequired
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      ...this.defaultUserState(),
      form: {
        isLoading: false
      },
      allTeams: [],
      submit: null,
      load: hangPromise(),
      saved: false,
      mfaLoad: mfaService.hasFlag(),
      isCountryNew: false
    }
  }

  defaultUserState = () => {
    return {
      firstName: null,
      lastName: null,
      userGroupId: null,
      email: null,
      language: authentication.userLocale(),
      access: UserRole.user,
      externalUserId: "",
      teams: [],
      primaryTeamId: null
    }
  }

  componentDidMount() {
    const load = teamService.list(authentication.userTenant().uuid).then((allTeams) => {
      this.setState({allTeams})
    })
    this.setState({load})
  }

  updateInput = (label: string, value: any) => {
    this.setState({[label]: value})
  }

  handleChange = (label: string, value: string) => {
    const trimmed = isString(value) ? value.trim() : value
    this.setState({[label]: trimmed})
  }

  handleUpdateCountry = (value: string, {isCountryNew}: {isCountryNew: boolean}) => {
    this.handleChange("userGroupId", value)
    this.setState({isCountryNew})
  }

  createUser = () => {
    this.setState(
      {
        form: {
          isLoading: true
        }
      },
      () => {
        const {email, firstName, lastName, teams, userGroupId, language, externalUserId, access, primaryTeamId, isCountryNew} = this.state
        const businessId = authentication.business().uuid

        const user: NewUserInput = {
          businessId,
          email: email!,
          firstName: firstName!,
          lastName: lastName!,
          externalUserId,
          language: language || authentication.userLocale()
        }
        const submit = usersService.create({user}).then(async(createdUser) => {
          if (access === UserRole.admin) {
            await usersService.makeUserGlobalAdmin(createdUser.uuid)
          }
          if (userGroupId) {
            if (isCountryNew) {
              await buService.createForRegion({businessId, regionId: userGroupId}).then(() => {
                adminTracking.adminPeopleConfirmAddCountry(MANUAL_ADD_SOURCE)
              })
            }
            const {teamId} = await buService.getForRegionId({businessId, regionId: userGroupId})
            await teamService.addUserToTeam(teamId, createdUser.uuid)
          }
          if (teams.length > 0) {
            const teamPromises = teams.map(({value}) => teamService.addUserToTeam(value, createdUser.uuid))
            await Promise.all(teamPromises)
          }
          if (primaryTeamId) {
            adminTracking.setPrimaryTeam()
            await usersService.update({user: {uuid: createdUser.uuid, primaryTeamId}})
          }
          this.setState({saved: true})
          adminTracking.createUser("user", teams.map(({label}) => label))
        })

        this.setState({submit})
      }
    )
  }

  handleMFARetry = () => {
    this.setState({mfaLoad: mfaService.hasFlag()})
  }

  handlePopupConfirm = () => {
    const {gotoSuccess} = this.props
    if (gotoSuccess) {
      return gotoSuccess()
    }
    History.push(VIEW_PEOPLE, this.context.apps.ADMIN)
  }

  handleSetPrimaryTeam = (teamId: string) => {
    this.setState({primaryTeamId: teamId})
  }

  handleUpdateTeams = (value: {value: string, label: string}[]) => {
    const {primaryTeamId} = this.state
    if (primaryTeamId && !value.find((team) => team.value === primaryTeamId)) {
      this.setState({primaryTeamId: null})
    }
    this.updateInput("teams", value)
  }

  render() {
    const {formatMessage, goBack} = this.props
    const {
      email,
      userGroupId,
      teams,
      language,
      externalUserId,
      access,
      submit,
      load,
      allTeams,
      saved,
      mfaLoad,
      isCountryNew
    } = this.state
    const firstName = this.state.firstName || ""
    const lastName = this.state.lastName || ""
    const buttonText = isCountryNew
      ? "admin.containers.addOnePerson.newCountryButton"
      : "admin.containers.addOnePerson.validationButton.text"

    return (
      <>
        <Loading load={mfaLoad} Error={() => <YouNeedToMfa onRetry={this.handleMFARetry} />}>
          <div className="addOnePerson">
            <Loading load={load}>
              {goBack
                ? <IconButton onClick={goBack} icon="backArrow" />
                : <BackLink />
              }
              <H2 marginBottom={4}>
                <FormattedMessage id="admin.containers.addOnePerson.heading" />
              </H2>
              <Row>
                <Col md={8}>
                  <Form>
                    <H4 marginBottom={2}>
                      <FormattedMessage id="admin.components.personalDetails.title" />
                    </H4>
                    <Row>
                      <Col md={5}>
                        <Firstname value={firstName} onChange={(value: string) => this.handleChange("firstName", value)} />
                      </Col>
                      <Col md={7}>
                        <Lastname value={lastName} onChange={(value: string) => this.handleChange("lastName", value)} />
                      </Col>
                    </Row>
                    <Email value={email} onChange={(value: string) => this.handleChange("email", value)} />
                    <Hr />
                    <H4 marginBottom={2}>
                      <FormattedMessage id="admin.components.perkboxSettings.title" />
                    </H4>
                    <BURegion
                      separateExisting
                      onChange={this.handleUpdateCountry}
                      value={userGroupId}
                      businessId={authentication.business().uuid}
                      source={MANUAL_ADD_SOURCE}
                      hint={<FormattedMessage id="platform.fields.country.hint" />} />
                    <Select
                      label={formatMessage({id: "admin.containers.addOnePerson.fields.role.label.header"})}
                      options={RoleOptions(formatMessage)}
                      value={access}
                      onChange={(value: {value: string, label: string}) => this.updateInput("access", value)} />
                    <Locale value={language} onChange={(value: string) => this.handleChange("language", value)} />
                    {allTeams && allTeams.length > 0 && (
                      <>
                        <Teams
                          isCreatable
                          value={teams}
                          onChange={this.handleUpdateTeams}
                          options={allTeams.map((team) => ({value: team.uuid, label: team.teamName}))} />
                        {/* <PrimaryTeam
                          value={primaryTeamId}
                          onChange={this.handleSetPrimaryTeam}
                          options={teams} /> */}
                      </>
                    )}
                    <ExternalID
                      value={externalUserId}
                      hint={<FormattedMessage id="admin.containers.addOnePerson.fields.externalId.hint" />}
                      onChange={(value: string) => this.handleChange("externalUserId", value)} />
                    <div>
                      <Loading load={submit} useLoader={false} />
                    </div>
                    <ValidationButton primary large onClick={this.createUser} load={submit}>
                      <FormattedMessage id={buttonText} values={{firstName}} />
                    </ValidationButton>
                  </Form>
                </Col>
              </Row>
            </Loading>
          </div>
        </Loading>
        <GenericModal
          title={formatMessage({id: "admin.containers.addOnePerson.popup.title"}, {name: firstName})}
          critical
          image={successImage}
          show={saved}
          proceedButtonText="admin.containers.addOnePerson.popup.button"
          onHide={this.handlePopupConfirm}
          onConfirm={this.handlePopupConfirm}
          noValidation>
          <Para large>
            <FormattedMessage id="admin.containers.addOnePerson.popup.text" values={{name: firstName}} />
          </Para>
        </GenericModal>
      </>
    )
  }
}

export default injectIntl(AddOnePerson)
